import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, catchError, Observable, tap, throwError } from 'rxjs';

import { environment } from '../../../environments/environment';
import {
  CalendarStatusModel,
  CalendarDisconnectResponse,
  CalendarConnectResponse,
  MeetingPreferenceResponse,
  MeetingPreferenceUpdateResponse
} from '../models/calendar-status.model';

@Injectable({
  providedIn: 'root',
})
export class CalendarService {
  private apiServerUrl: string = environment.apiBaseUrl;
  public preferredMeetingProvider$: BehaviorSubject<string | null> = new BehaviorSubject<string | null>(null);

  constructor(private http: HttpClient) {}

  /**
   * Get current calendar connection status
   */
  public getCalendarStatus(): Observable<CalendarStatusModel> {
    return this.http.get<CalendarStatusModel>(
      `${this.apiServerUrl}/calendar/status`
    );
  }

  /**
   * Initiate Google Calendar connection - returns OAuth URL
   * Make AJAX call to get authUrl, then redirect to it
   */
  public initiateGoogleConnect(): Observable<CalendarConnectResponse> {
    return this.http.get<CalendarConnectResponse>(
      `${this.apiServerUrl}/calendar/google/connect`
    );
  }

  /**
   * Initiate Microsoft Calendar connection - returns OAuth URL
   * Make AJAX call to get authUrl, then redirect to it
   */
  public initiateMicrosoftConnect(): Observable<CalendarConnectResponse> {
    return this.http.get<CalendarConnectResponse>(
      `${this.apiServerUrl}/calendar/microsoft/connect`
    );
  }

  /**
   * Disconnect Google Calendar
   */
  public disconnectGoogleCalendar(): Observable<CalendarDisconnectResponse> {
    return this.http.post<CalendarDisconnectResponse>(
      `${this.apiServerUrl}/calendar/google/disconnect`,
      {}
    );
  }

  /**
   * Disconnect Microsoft Calendar
   */
  public disconnectMicrosoftCalendar(): Observable<CalendarDisconnectResponse> {
    return this.http.post<CalendarDisconnectResponse>(
      `${this.apiServerUrl}/calendar/microsoft/disconnect`,
      {}
    );
  }

  /**
   * Get current meeting provider preference
   */
  public getMeetingPreference(): Observable<MeetingPreferenceResponse> {
    return this.http.get<MeetingPreferenceResponse>(
      `${this.apiServerUrl}/calendar/meeting-preference`
    ).pipe(
      catchError(error => {
        return throwError(() => new Error(error));
      }),
      tap((response: MeetingPreferenceResponse): void => {
          this.preferredMeetingProvider$.next(response.preferredMeetingProvider);
          })
      );
  }

  /**
   * Set preferred meeting provider (GOOGLE_MEET or MICROSOFT_TEAMS)
   */
  public setMeetingPreference(provider: string): Observable<MeetingPreferenceUpdateResponse> {
    return this.http.put<MeetingPreferenceUpdateResponse>(
      `${this.apiServerUrl}/calendar/meeting-preference?provider=${provider}`,
      {}
    ).pipe(
      catchError(error => {
        return throwError(() => new Error(error));
      }),
      tap((response: MeetingPreferenceUpdateResponse): void => {
          this.preferredMeetingProvider$.next(response.preferredMeetingProvider);
          })
      );
  }

  /**
   * Clear meeting provider preference (revert to default)
   */
  public clearMeetingPreference(): Observable<{ status: string }> {
    return this.http.delete<{ status: string }>(
      `${this.apiServerUrl}/calendar/meeting-preference`
    ).pipe(
      catchError(error => {
        return throwError(() => new Error(error));
      }),
      tap(() => this.preferredMeetingProvider$.next(null))
    );
  }

  public updateMeetingProvider(provider: string | null): void {
    this.preferredMeetingProvider$.next(provider);
  }
}
